import { types, flow, Instance } from 'mobx-state-tree';
import { snakeCase, pickBy } from 'lodash';

import { HiringTeams } from '@src/services/companies';
import { getCompanyDomain } from '@src/helpers';
import { getPermissions } from 'src/services/resources';
import { toast } from 'src/components';

const TeamMember = types.model('TeamMember', {
  id: types.identifierNumber,
  email: types.string,
  type: types.string,
  active: types.boolean,
  full_photo_url: types.maybeNull(types.string),
  initials: types.maybeNull(types.string),
  full_name: types.maybeNull(types.string),
  permission: types.maybeNull(types.string),
  status: types.string
});

const Permissions = types.model('Permissions', {
  admin: types.number,
  recruitment_manager: types.number,
  recruiter: types.number,
  freelancer: types.maybeNull(types.number)
});

export const HiringTeamsStore = types
  .model('HiringTeamsStore', {
    isFetching: types.optional(types.boolean, false),
    permissions: types.frozen(),
    members: types.optional(types.array(TeamMember), []),
    jobs: types.optional(types.array(types.frozen()), []),
    inviteErrors: types.frozen(),
    showProgressLoader: types.optional(types.boolean, false)
  })
  .actions(self => {
    return {
      fetchHiringTeam: flow(function* fetch(qs = '') {
        const companyId = getCompanyDomain();

        try {
          const res = yield HiringTeams.getTeamMembers(companyId, qs);

          self.members = res;
        } catch (error) { }
      }),
      fetchJobs: flow(function* fetch() {
        const companyId = getCompanyDomain();

        try {
          const res = yield HiringTeams.getJobs(companyId);
          self.jobs = res;
        } catch (error) { }
      }),
      fetchPermissions: flow(function* fetch() {
        try {
          const permissions = yield getPermissions();

          self.permissions = permissions;
        } catch (error) { }
      })
    };
  })
  .actions(self => {
    return {
      fetchData: flow(function* fetch(qs) {
        self.isFetching = true;

        try {
          yield self.fetchHiringTeam(qs);
          yield self.fetchPermissions();
          yield self.fetchJobs();
          self.isFetching = false;
        } catch (error) {
          self.isFetching = false;
        }
      }),
      switchActiveStatus: flow(function* fetch(payload: Array<number>, type) {
        const companyId = getCompanyDomain();
        self.showProgressLoader = true;

        try {
          // facepalm.jpg
          if (type === 'cancelInvite') {
            yield HiringTeams.cancelInvites(companyId, payload);
          }
          if (type === 'deactivate') {
            yield HiringTeams.deactivateStatus(companyId, payload);
          }
          if (type === 'activate') {
            yield HiringTeams.activateStatus(companyId, payload);
          }

          yield self.fetchHiringTeam();
          self.showProgressLoader = false;
        } catch (error) {
          self.showProgressLoader = false;
        }
      }),
      changeRole: flow(function* fetch(userId: number, permission: number) {
        const companyId = getCompanyDomain();
        self.showProgressLoader = true;

        try {
          yield HiringTeams.changeRole({ companySlug: companyId, userId, permission });

          yield self.fetchHiringTeam();
          self.showProgressLoader = false;
        } catch (error) {
          self.showProgressLoader = false;
        }
      }),
      resendInvite: flow(function* fetch(ids: Array<number>) {
        const companyId = getCompanyDomain();
        self.showProgressLoader = true;

        try {
          yield HiringTeams.resendInvitations({ companySlug: companyId, ids });
          toast.info('Success');
          yield self.fetchHiringTeam();

          self.showProgressLoader = false;
        } catch (error) {
          self.showProgressLoader = false;
        }
      }),
      clearInviteErrors: () => {
        self.inviteErrors = {};
      },
      inviteTeammate: flow(function* fetch(invites, cb) {
        const companyId = getCompanyDomain();
        self.showProgressLoader = true;

        try {
          yield HiringTeams.inviteTeammate(
            companyId,
            invites.map(invite => {
              return pickBy(
                {
                  email: invite.email,
                  permission: self.permissions[snakeCase(invite.role)],
                  job_id: invite?.job
                },
                v => v !== undefined || v !== null
              );
            })
          );

          cb();
          self.clearInviteErrors();
          yield self.fetchHiringTeam();
          self.showProgressLoader = false;
        } catch (error) {
          self.showProgressLoader = false;
          self.inviteErrors = { errorsList: error.messages, inviteErrorsTitle: error.notice };
        }
      })
    };
  });

export type ITeamMember = Instance<typeof TeamMember>;
export type IHiringTeamStore = Instance<typeof HiringTeamsStore>;
export type IPermissions = Instance<typeof Permissions>;
